import React, { useState, useEffect, useRef } from 'react';
import {
  CalendarMonth,
  AttachMoney,
  Description,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';
import { Modal } from '@components/molecules/Modal';
import { Button } from '@components/atoms/Button';
import { Input } from '@components/atoms/Input';
import { Card } from '@components/atoms/Card';
import { SmallDocumentList } from '@components/molecules/SmallDocumentList';
import { FileDropZone } from '@components/molecules/FileDropZone';
import { BudgetRequest, BudgetProposalCreateInput } from '@models/budget.model';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { format } from 'date-fns';
import { parseTaskDate, formatTaskDateForBackend } from '@utils/dateOnlyParser';

interface BudgetResponseModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: BudgetProposalCreateInput) => void;
  onAccept?: () => void;
  onReject?: () => void;
  budgetRequest: BudgetRequest | null;
  isSubmitting: boolean;
}

const validationSchema = Yup.object({
  proposedStartDate: Yup.date()
    .required('Start date is required')
    .typeError('Invalid date'),
  proposedEndDate: Yup.date()
    .required('End date is required')
    .min(Yup.ref('proposedStartDate'), 'End date must be after start date')
    .typeError('Invalid date'),
  // Sprint 23, story-110 (QVIKLDSG). Boundary and message text are binding per
  // docs/ux/story-110-design.md: ALLOW <= $99,999,999.99, REJECT > $99,999,999.99. Message must
  // stay byte-identical to the backend's BudgetProposalResolver.BID_CEILING_MESSAGE so client and
  // server never disagree.
  proposedAmount: Yup.number()
    .required('Budget amount is required')
    .positive('Amount must be positive')
    .max(99_999_999.99, "Bid amounts over $99,999,999.99 aren't supported. Enter an amount of $99,999,999.99 or less.")
    .typeError('Invalid amount'),
  description: Yup.string(),
});

const BudgetResponseModal: React.FC<BudgetResponseModalProps> = ({
  open,
  onClose,
  onSubmit,
  onAccept,
  onReject,
  budgetRequest,
  isSubmitting,
}) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const documentsListRef = useRef<HTMLDivElement>(null);

  // Inject z-index override to ensure response modal appears above everything
  useEffect(() => {
    if (open) {
      const styleId = 'budget-response-modal-z-index-override';
      let style = document.getElementById(styleId) as HTMLStyleElement;

      if (!style) {
        style = document.createElement('style');
        style.id = styleId;
        document.head.appendChild(style);
      }

      // Use extremely high z-index values to ensure response modal appears above everything
      // Response modal needs to be above counter proposal modal (199999/200000)
      style.textContent = `
        /* Override z-index for budget response modal - target ReactModal elements */
        .ReactModal__Overlay:has(.budget-response-modal-wrapper) {
          z-index: 299999 !important;
        }
        .ReactModal__Content:has(.budget-response-modal-wrapper) {
          z-index: 300000 !important;
        }
      `;

      return () => {
        if (style && style.parentNode) {
          style.parentNode.removeChild(style);
        }
      };
    }
  }, [open]);

  // Auto-scroll to document list when files are attached
  useEffect(() => {
    if (selectedFiles.length > 0 && documentsListRef.current) {
      setTimeout(() => {
        documentsListRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }, 100);
    }
  }, [selectedFiles.length]);

  const formatCurrency = (amount?: number): string => {
    if (!amount) return 'Not specified';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handleClose = () => {
    if (isUploading) return; // Prevent closing during upload
    setSelectedFiles([]);
    onClose();
  };

  const formik = useFormik({
    initialValues: {
      proposedStartDate: budgetRequest?.requestedStartDate
        ? formatTaskDateForBackend(
            parseTaskDate(budgetRequest.requestedStartDate),
          ) || ''
        : '',
      proposedEndDate: budgetRequest?.requestedEndDate
        ? formatTaskDateForBackend(
            parseTaskDate(budgetRequest.requestedEndDate),
          ) || ''
        : '',
      proposedAmount: '',
      description: '',
    },
    validationSchema,
    onSubmit: (values) => {
      if (!budgetRequest) return;

      const input: BudgetProposalCreateInput = {
        budgetRequestId: budgetRequest.budgetRequestId,
        proposedStartDate: values.proposedStartDate,
        proposedEndDate: values.proposedEndDate,
        proposedAmount: parseFloat(values.proposedAmount),
        description: values.description || undefined,
        files: selectedFiles.length > 0 ? selectedFiles : undefined,
      };

      if (selectedFiles.length > 0) {
        setIsUploading(true);
      }

      try {
        onSubmit(input);
      } finally {
        setIsUploading(false);
      }
    },
    enableReinitialize: true,
  });

  if (!budgetRequest) return null;

  return (
    <Modal
      isOpen={open}
      onClose={handleClose}
      size="large"
      title="Respond to Bid Request"
      showCloseButton={true}
      contentClassName="!max-h-[85vh]"
      handleConfirm={() => formik.handleSubmit()}
      confirmText={
        isSubmitting
          ? 'Submitting...'
          : isUploading
            ? 'Uploading...'
            : 'Submit Proposal'
      }
      disableConfirm={isSubmitting || isUploading || !formik.isValid}
      isLoading={isSubmitting || isUploading}>
      <div className="pt-5 budget-response-modal-wrapper">
        <form onSubmit={formik.handleSubmit}>
          {/* Request Details */}
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-gray-500 mb-2">
              Request Details
            </h4>
            <Card className="bg-amber-50 p-4">
              <h5 className="text-sm font-semibold text-gray-900 mb-2">
                {budgetRequest.task?.name || 'Budget Request'}
              </h5>
              {budgetRequest.task?.project && (
                <p className="text-xs text-gray-500 mb-1">
                  Project: {budgetRequest.task.project.name}
                </p>
              )}
              {budgetRequest.description && (
                <p className="text-sm text-gray-600 mt-2 mb-3">
                  {budgetRequest.description}
                </p>
              )}
              <div className="flex flex-wrap gap-4 text-xs text-gray-500">
                <span>
                  Requested:{' '}
                  {format(
                    new Date(budgetRequest.requestedStartDate),
                    'MMM dd, yyyy',
                  )}{' '}
                  -
                  {format(
                    new Date(budgetRequest.requestedEndDate),
                    'MMM dd, yyyy',
                  )}
                </span>
                <span>
                  Max Bid: {formatCurrency(budgetRequest.maxBudgetAmount)}
                </span>
              </div>
              {budgetRequest.documents && budgetRequest.documents.length > 0 && (
                <div className="mt-3">
                  <p className="text-xs text-gray-500 mb-2">Attached Documents</p>
                  <SmallDocumentList
                    documents={budgetRequest.documents}
                    showDelete={false}
                  />
                </div>
              )}
            </Card>
          </div>

          {/* Quick Actions - Only show if there are actions available */}
          {((onAccept && budgetRequest.maxBudgetAmount) || onReject) && (
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-gray-500 mb-3">
                Quick Actions
              </h4>
              <Card className="p-4 bg-slate-50 border border-slate-200">
                <div className="flex flex-wrap gap-3">
                  {onAccept && budgetRequest.maxBudgetAmount && (
                    <Button
                      type="button"
                      variant="primary"
                      leftIcon={<CheckCircle className="w-4 h-4" />}
                      onClick={onAccept}
                      disabled={isSubmitting}
                      className="bg-green-600 hover:bg-green-700 text-white">
                      Accept Request As Is
                    </Button>
                  )}
                  {onReject && (
                    <Button
                      type="button"
                      variant="secondary"
                      leftIcon={<Cancel className="w-4 h-4" />}
                      onClick={onReject}
                      disabled={isSubmitting}
                      className="border-red-500 text-red-500 hover:bg-red-50">
                      Reject Request
                    </Button>
                  )}
                </div>
              </Card>
            </div>
          )}

          {/* Proposal Form */}
          <div className="space-y-5">
            <h4 className="text-sm font-semibold text-gray-500">
              Or Submit Your Own Proposal
            </h4>

            {/* Date Range */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  Start Date
                </label>
                <div className="relative">
                  <CalendarMonth className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-amber-500" />
                  <Input
                    type="date"
                    name="proposedStartDate"
                    value={formik.values.proposedStartDate}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className="pl-10 w-full"
                    error={
                      formik.touched.proposedStartDate &&
                      Boolean(formik.errors.proposedStartDate)
                    }
                  />
                </div>
                {formik.touched.proposedStartDate &&
                  formik.errors.proposedStartDate && (
                    <p className="text-red-500 text-sm">
                      {formik.errors.proposedStartDate}
                    </p>
                  )}
              </div>
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  End Date
                </label>
                <div className="relative">
                  <CalendarMonth className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-amber-500" />
                  <Input
                    type="date"
                    name="proposedEndDate"
                    value={formik.values.proposedEndDate}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className="pl-10 w-full"
                    error={
                      formik.touched.proposedEndDate &&
                      Boolean(formik.errors.proposedEndDate)
                    }
                  />
                </div>
                {formik.touched.proposedEndDate &&
                  formik.errors.proposedEndDate && (
                    <p className="text-red-500 text-sm">
                      {formik.errors.proposedEndDate}
                    </p>
                  )}
              </div>
            </div>

            {/* Budget Amount */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                Proposed Budget Amount
              </label>
              <div className="relative">
                <AttachMoney className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-amber-500" />
                <Input
                  type="number"
                  name="proposedAmount"
                  value={formik.values.proposedAmount}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  placeholder="Enter amount..."
                  className="pl-10 w-full"
                  error={
                    formik.touched.proposedAmount &&
                    Boolean(formik.errors.proposedAmount)
                  }
                />
              </div>
              {formik.touched.proposedAmount &&
                formik.errors.proposedAmount && (
                  <p className="text-red-500 text-sm">
                    {formik.errors.proposedAmount}
                  </p>
                )}
            </div>

            {/* Description */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                Description (Optional)
              </label>
              <div className="relative">
                <Description className="absolute left-3 top-3 w-5 h-5 text-amber-500" />
                <textarea
                  name="description"
                  value={formik.values.description}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  rows={4}
                  placeholder="Enter description..."
                  className={`pl-10 w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none ${
                    formik.touched.description && formik.errors.description
                      ? 'border-red-500'
                      : 'border-gray-300'
                  }`}
                />
              </div>
              {formik.touched.description && formik.errors.description && (
                <p className="text-red-500 text-sm">
                  {formik.errors.description}
                </p>
              )}
            </div>

            {/* File Upload Section */}
            <div ref={documentsListRef}>
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-3">
                Attach Additional Documents (Optional)
              </h4>
              <FileDropZone
                files={selectedFiles}
                onFilesChange={setSelectedFiles}
                disabled={isUploading}
                isUploading={isUploading}
                activeColor="indigo"
              />
            </div>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default BudgetResponseModal;
